import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';
import type { WebXRAbstractMotionController } from '@babylonjs/core/XR/motionController/webXRAbstractMotionController';
import type { WebXRDefaultExperience } from '@babylonjs/core/XR/webXRDefaultExperience';
import type { WebXRInputSource } from '@babylonjs/core/XR/webXRInputSource';

import { handedness, type HandRole } from './Handedness';

/**
 * One model of "a thing that can act", over two very different inputs: motion controllers and
 * a desktop mouse. Tracked hands used to be a third — removed outright, 2026-08-10, on the
 * owner's own call: "the whole 'hand' mode support... doesn't work well in the first place,
 * and is a very awkward fit in this system. We should require a controller for all actions."
 * See CLAUDE.md's "Grip means near, trigger means far" entry for what that leaves behind.
 *
 * Everything downstream — possession, the tray, connection drawing — reads only this.
 * Without it each of those would need two code paths. The desktop path used to be the only
 * way to test multiplayer without two headsets; it no longer is, now that spectator mode joins
 * every desktop session as a spectator unconditionally (`main.ts`) and never starts
 * `AuthoringController` for it — two desktop tabs still prove presence, avatars and pose
 * streaming, but cannot grab a node, take a lock, place a shape or draw a stroke. See
 * README.md's "Testing multiplayer without two headsets" for the corrected account, and
 * CLAUDE.md's "two browser tabs stopped being able to test locks or possession at all" entry
 * for when and why. This `Grabber` construction itself is unaffected — the desktop pointer
 * grabber below still exists and is still driven by the automated suite directly.
 *
 * Two signals, still, though what they mean has changed. **Grip means near, trigger means
 * far** was the whole of the control scheme through M6–M14: squeeze to take hold of what you
 * are touching, pull the trigger to act on what you are aiming at, two physical buttons so one
 * press could never mean both. **Reversed, 2026-08-10** (plan/mode-hierarchy.md, "Grabbing
 * moves from the dominant grip to the dominant trigger; near/far is gone" — settled): the
 * dominant trigger now does both jobs — grab what is within reach, or act on what is aimed at
 * beyond it. That was disambiguated by proximity for one day, as a deliberate interim stopgap
 * ahead of Link mode existing; Link mode's own gesture landed the same day and retired the
 * stopgap for good — grabbing and linking are now confined to disjoint modes (Build's trigger
 * only ever grabs, Link's only ever links), so which mode you are in is what disambiguates a
 * press, not proximity. See `ConnectionDrawer.ts`'s own module doc for the retirement. The
 * dominant grip is free as of this change, reserved for a later mode-switch
 * gesture (grip held plus a stick flick); `near`/`far` still exist as fields because the off
 * hand still uses both (grip opens the tray, trigger confirms a selection), and because
 * `separatesNearFar` below is still real for a hypothetical controller profile with no squeeze.
 * See CLAUDE.md's own "Grip means near, trigger means far" entry for the fuller account of the
 * reversal.
 */

export type GrabberKind = 'controller' | 'pointer';

export interface ButtonState {
  readonly pressed: boolean;

  /** Rose this frame. */
  readonly justPressed: boolean;

  /** Fell this frame. */
  readonly justReleased: boolean;
}

export interface Grabber {
  readonly id: string;
  readonly kind: GrabberKind;
  readonly handedness: XRHandedness;

  /** Resolved live, so flipping handedness moves every binding without a reload. */
  readonly role: HandRole;

  /** Where a *near* action happens: the pinch point, the grip pose, the cursor's target. */
  readonly position: Vector3;
  readonly rotation: Quaternion;

  /** Where a *far* action is aimed from. Not the same place as `position`. */
  readonly aimOrigin: Vector3;
  readonly forward: Vector3;

  /**
   * Orientation to go with `aimOrigin`/`forward` — completes the far pose the way `rotation`
   * completes `position` for the near one: without it, nothing can orient a held object to the
   * pointer pose instead of the grip.
   *
   * No current consumer: `PenLedge`, the thing this was built for (a held pen wants to point
   * where the aim pose points, not however the fist happens to be angled), is deleted
   * (plan/history/control-modes.md, "Remove the pen ledge entirely") — pens and the eraser are no
   * longer held props, so nothing needs their orientation any more. Left computed rather than
   * removed, the same call made for `XrManager.setDominantControllerModelVisible`: correct,
   * cheap, and exactly what a future held-and-oriented prop would need again.
   */
  readonly aimRotation: Quaternion;

  /**
   * Grip, or a plain drag. On the dominant hand this is free as of 2026-08-10 (nothing reads
   * it to grab any more — plan/mode-hierarchy.md, "Grabbing moves from the dominant grip to
   * the dominant trigger"), reserved for a later mode-switch gesture. On the off hand it is
   * unchanged: held, it shows the tray.
   */
  readonly near: ButtonState;

  /**
   * Trigger, or a shift-drag. On the dominant hand this is the one button each mode reads for
   * the thing that mode does with a press — grab what is within reach in Build
   * (`PossessionManager.considerGrab`), draw or aim a connection in Link (`ConnectionDrawer.tick`,
   * gated on `ToolMode.isLinkMode`) — with which mode you are in deciding which, not a
   * proximity test: grabbing and linking are confined to disjoint modes, so the two can never
   * disagree about the same press. On the off hand this confirms whatever the tray's own stick
   * navigation is currently highlighting.
   */
  readonly far: ButtonState;

  /**
   * Whether `near` and `far` are separate physical inputs.
   *
   * False for a controller profile with no squeeze component — the trigger then serves both
   * roles for *everything* that button does. `ConnectionDrawer.nodeUnderAim` and
   * `edgeUnderAim` both still read this to widen the minimum aim distance (`MIN_AIM_DISTANCE`
   * vs `MIN_AIM_DISTANCE_EXPLICIT`) for exactly that profile — a squeeze-less controller has
   * nothing to make its off-hand tray-confirm gesture unambiguous either, the same reasoning
   * this flag always existed for. Tracked hands used to be the other source of `false` here,
   * and the more common one — a pinch cannot say which it meant, either — but hand tracking is
   * gone (2026-08-10; see this file's own module doc). No controller profile shipped with this
   * project actually lacks a squeeze, so in practice this is always `true` today; the flag and
   * the fallback it drives stay, because the case is still real for a profile that could. This
   * flag's own fallback briefly carried a second job, for one day: disambiguating grab from
   * connect by distance while both lived on the same dominant trigger in the same mode. Link
   * mode's own gesture retired that stopgap (`ConnectionDrawer.ts`'s own module doc) — which
   * mode you are in disambiguates a trigger press now, not proximity — so this flag is back to
   * describing only what it always did: whether a squeeze-less profile's aim distance needs
   * widening.
   */
  readonly separatesNearFar: boolean;

  /**
   * Thumbstick axes, controller only. Null for the desktop pointer, which has none — which is
   * how the off-hand tray tells whether it should navigate itself by stick or fall back to the
   * dominant-hand reach for that grabber. A capability the binding layer reports, the same way
   * `separatesNearFar` does, rather than a kind check repeated wherever something wants to
   * know.
   */
  readonly stick: { x: number; y: number } | null;

  /**
   * Requests a haptic pulse on this grabber's own controller — Babylon's
   * `WebXRAbstractMotionController.pulse(intensity 0..1, durationMs)`, itself a thin wrapper
   * over the WebXR gamepad's (experimental) `hapticActuators`. Undefined for the desktop
   * pointer, which has nothing to vibrate — the same "capability the binding layer reports"
   * shape `stick` above already uses, so a caller checks presence once
   * (`grabber.pulse?.(...)`) rather than branching on `kind` itself. Present but a silent
   * no-op for a controller whose motion controller has not finished loading yet, or whose
   * profile genuinely has no actuator — Babylon's own `pulse` already "fails silently and
   * returns Promise<false>" for that case, so nothing here has to re-detect it.
   * Fire-and-forget: the returned promise is not exposed, since every caller today only cares
   * that the request was made, not when the pulse finishes (DrawingManager.ts, "Haptics on
   * board contact").
   */
  readonly pulse?: (intensity: number, durationMs: number) => void;

  /** True while tracking is live. An untracked controller keeps its last pose. */
  readonly tracked: boolean;

  /**
   * The controller's primary face button — X on a Touch left controller, A on the right;
   * same relative position (WebXR component id `x-button`/`a-button`, gamepad button index
   * 4 either way — confirmed by reading the Oculus Touch profile Babylon actually ships,
   * `webXROculusTouchMotionController.js`), different letter depending on which hand this
   * grabber currently is. Null for the desktop pointer, which has no face button at all —
   * the first binding in this project that does not resolve for every `Grabber`, the same "a
   * capability the binding layer reports, absent rather than faked" shape `stick` already uses
   * for whatever input genuinely lacks one. Every caller has to handle the null.
   *
   * Resolved identically for either hand — this is a description of a physical button, not
   * an off-hand-only concept — so two different callers read it off two different roles for
   * two different meanings: `AssistantController.tick` reads it off the *off* hand to open
   * an assistant turn (plan/ai-in-room.md), and `NodeLabelController.tick` reads it off the
   * *dominant* hand, while a node is held, to speak that node's label (plan/open-items.md).
   * Nothing stops a third caller from reading a third role's copy of the same field for a
   * fourth meaning; disambiguating what a press *means* is each caller's own job, not this
   * binding layer's.
   */
  readonly talkButton: ButtonState | null;

  /**
   * The controller's secondary face button — Y on the left, B on the right (component id
   * `y-button`/`b-button`, gamepad index 5). Toggles the microphone
   * (plan/ai-in-room.md). Same nullability as `talkButton`, for the same reason.
   */
  readonly micToggleButton: ButtonState | null;
}

class MutableButton implements ButtonState {
  pressed = false;
  justPressed = false;
  justReleased = false;

  set(next: boolean): void {
    this.justPressed = next && !this.pressed;
    this.justReleased = !next && this.pressed;
    this.pressed = next;
  }
}

class MutableGrabber implements Grabber {
  position = Vector3.Zero();
  rotation = Quaternion.Identity();
  aimOrigin = Vector3.Zero();
  forward = Vector3.Forward();
  aimRotation = Quaternion.Identity();

  readonly near = new MutableButton();
  readonly far = new MutableButton();

  separatesNearFar = false;
  tracked = false;
  stick: { x: number; y: number } | null = null;
  talkButton: MutableButton | null = null;
  micToggleButton: MutableButton | null = null;

  /**
   * The live motion controller backing `pulse` below, refreshed every tick by
   * `updateFromController` — not read directly by anything outside this class. Stays null
   * for a pointer grabber (never assigned there) and, for a controller, until its profile
   * finishes loading.
   */
  motionController: WebXRAbstractMotionController | null = null;

  readonly pulse: ((intensity: number, durationMs: number) => void) | undefined;

  constructor(
    readonly id: string,
    readonly kind: GrabberKind,
    readonly handedness: XRHandedness,
  ) {
    // Fixed for this grabber's lifetime, same as `kind` itself: only a controller ever has an
    // actuator to ask for, so only a controller-kind grabber gets a `pulse` function at all —
    // the desktop pointer keeps it undefined, per this field's own doc on `Grabber`. The
    // closure reads `this.motionController` fresh on every call rather than capturing it now,
    // since it is only set (and can start, then later load) well after this constructor
    // returns.
    this.pulse =
      kind === 'controller'
        ? (intensity: number, durationMs: number) => {
            void this.motionController?.pulse(intensity, durationMs);
          }
        : undefined;
  }

  get role(): HandRole {
    return handedness.roleOf(this.handedness);
  }

  clearButtons(): void {
    this.near.set(false);
    this.far.set(false);
  }
}

export class GrabberHub {
  private readonly xrGrabbers = new Map<string, MutableGrabber>();
  private readonly pointer = new MutableGrabber('pointer', 'pointer', 'none');

  private experience: WebXRDefaultExperience | null = null;

  /** Set by DevSim; keeps mouse plumbing out of here. */
  pointerActive = false;

  constructor(private readonly scene: Scene) {
    // A mouse has two distinct gestures — drag and shift-drag — so it says which it meant.
    this.pointer.separatesNearFar = true;
  }

  /** Every grabber currently tracked, XR or desktop. */
  get grabbers(): Grabber[] {
    const active: Grabber[] = [...this.xrGrabbers.values()].filter((g) => g.tracked);

    if (this.pointerActive) {
      active.push(this.pointer);
    }

    return active;
  }

  byHandedness(handedness: XRHandedness): Grabber | undefined {
    return this.grabbers.find((grabber) => grabber.handedness === handedness);
  }

  /** The tracked input playing this role. Undefined if that controller is not present. */
  byRole(role: HandRole): Grabber | undefined {
    return this.grabbers.find((grabber) => grabber.role === role);
  }

  /** Drives the desktop grabber. Called by DevSim from mouse events. */
  updatePointer(
    position: Vector3,
    aimOrigin: Vector3,
    forward: Vector3,
    near: boolean,
    far: boolean,
  ): void {
    this.pointer.position.copyFrom(position);
    this.pointer.aimOrigin.copyFrom(aimOrigin);
    this.pointer.forward.copyFrom(forward);

    // A mouse ray has no pose of its own to roll around, so this uses the plain world-up
    // reference — see aimRotationFromForward's own doc.
    this.pointer.aimRotation.copyFrom(aimRotationFromForward(forward));
    this.pointer.tracked = true;
    this.pointer.near.set(near);
    this.pointer.far.set(far);
  }

  attachXr(experience: WebXRDefaultExperience): void {
    this.experience = experience;

    experience.input.onControllerAddedObservable.add((controller) => {
      const id = controller.uniqueId;
      this.xrGrabbers.set(id, new MutableGrabber(id, 'controller', controller.inputSource.handedness));
    });

    experience.input.onControllerRemovedObservable.add((controller) => {
      this.xrGrabbers.delete(controller.uniqueId);
    });
  }

  /**
   * Refreshes every grabber. Called once per frame before anything reads them, so all
   * consumers see the same instant — polling per consumer would let a grip start be
   * observed by one and missed by another.
   */
  update(): void {
    const experience = this.experience;

    if (!experience) {
      return;
    }

    for (const controller of experience.input.controllers) {
      const grabber = this.xrGrabbers.get(controller.uniqueId);

      if (!grabber) {
        continue;
      }

      this.updateFromController(grabber, controller);
    }
  }

  private updateFromController(grabber: MutableGrabber, controller: WebXRInputSource): void {
    // Grip pose when the profile provides one; the pointer pose is a usable stand-in and
    // is always present.
    const grip = controller.grip ?? controller.pointer;

    grabber.position.copyFrom(grip.absolutePosition);
    grabber.rotation.copyFrom(grip.absoluteRotationQuaternion ?? Quaternion.Identity());

    // Origin *and* direction both from the pointer pose. They are different poses on a Touch
    // controller — the pointer sits forward of the fist and angled down — so taking the
    // origin from the grip and the direction from the pointer produced a ray that started at
    // the hand and ran along a line that missed whatever was being aimed at. The connection
    // preview still drew, because it falls back to a point along that ray, and then release
    // found nothing under the aim and cancelled. That was the "connections don't stick" bug.
    grabber.aimOrigin.copyFrom(controller.pointer.absolutePosition);
    grabber.forward.copyFrom(controller.pointer.forward);

    // Same node as aimOrigin/forward, so all three stay consistent by construction — falling
    // back to identity only when the profile has no rotation to report at all.
    grabber.aimRotation.copyFrom(controller.pointer.absoluteRotationQuaternion ?? Quaternion.Identity());

    grabber.tracked = true;

    const motionController = controller.motionController;

    // Kept current every tick, whichever way this turns out — asynchronous profile loading
    // means a controller can go from having none to having one partway through a session, and
    // `pulse` (constructed once, above) reads this field fresh on every call rather than
    // capturing today's value.
    grabber.motionController = motionController ?? null;

    if (!motionController) {
      grabber.clearButtons();
      grabber.stick = null;
      grabber.talkButton = null;
      grabber.micToggleButton = null;
      return;
    }

    const squeeze = motionController.getComponentOfType('squeeze');
    const trigger = motionController.getComponentOfType('trigger');

    // A profile with no squeeze has nothing else to offer, so the trigger serves both roles
    // and that controller falls back to telling near from far by distance.
    grabber.separatesNearFar = squeeze !== null && trigger !== null;

    grabber.near.set((squeeze ?? trigger)?.pressed ?? false);
    grabber.far.set(trigger?.pressed ?? false);

    // Same lookup XrManager uses for its own off-hand bindings: a thumbstick where the
    // profile has one, a touchpad stand-in otherwise. Read every frame rather than through
    // the component's own change observable, to match how everything else here is a snapshot
    // rather than an event.
    const thumbstick =
      motionController.getComponentOfType('thumbstick') ?? motionController.getComponentOfType('touchpad');

    if (thumbstick) {
      const stick = grabber.stick ?? (grabber.stick = { x: 0, y: 0 });
      stick.x = thumbstick.axes.x;
      stick.y = thumbstick.axes.y;
    } else {
      grabber.stick = null;
    }

    // getComponentOfType('button') is ambiguous here — a Touch controller profile has
    // three components of type "button" (the primary face button, the secondary one, and
    // thumbrest), so unlike squeeze/trigger/thumbstick this has to look up components by
    // their exact id and fall back across hands, the same "same physical position,
    // different letter" mapping recentre used to read this ambiguously (via
    // getComponentOfType('button'), which just happened to resolve to the primary face
    // button) in `XrManager.bindOffHandControls`, before that binding collided with this one
    // — both fired on the same physical press — and recentre was removed rather than fixed to
    // look the button up correctly. See CLAUDE.md and plan/ai-in-room.md.
    const talk = motionController.getComponent('x-button') ?? motionController.getComponent('a-button');
    const micToggle = motionController.getComponent('y-button') ?? motionController.getComponent('b-button');

    if (talk) {
      const button = grabber.talkButton ?? (grabber.talkButton = new MutableButton());
      button.set(talk.pressed);
    } else {
      grabber.talkButton = null;
    }

    if (micToggle) {
      const button = grabber.micToggleButton ?? (grabber.micToggleButton = new MutableButton());
      button.set(micToggle.pressed);
    } else {
      grabber.micToggleButton = null;
    }
  }

  /** Registers the per-frame update. Returns a disposer. */
  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.update());

    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }
}

/**
 * Builds an aim rotation from a `forward` direction, with roll — the rotation *about* that
 * direction — taken from world up. `forward` alone is a direction and has no opinion about
 * roll at all, so something else has to supply it.
 *
 * This used to take a second, independent `rollReference` argument: a tracked hand's wrist
 * carried its own independently-tracked rotation, separate from `forward` (index-tip minus
 * wrist), and a held prop's roll read from the wrist while its direction still came from
 * `forward` alone. Hand tracking is gone (2026-08-10; see this file's own module doc), and its
 * `updateFromHand` was the only caller that ever passed anything but `Quaternion.Identity()` —
 * the desktop pointer (`updatePointer`, the sole remaining caller) has no pose of its own to
 * roll around, so a mouse ray always used the plain world-up reference this function now
 * builds unconditionally. Collapsed back to the single-argument construction this function had
 * before hands existed, rather than carrying a parameter with only one possible value left.
 *
 * `Quaternion.FromLookDirectionLH` trusts its `up` argument to already be exactly orthogonal
 * to `forward` and does not correct it (confirmed by reading `Matrix.LookDirectionLHToRef`:
 * it takes the cross product straight into the result with no normalisation or
 * re-orthogonalisation step), so passing world up unmodified would misbuild the basis for
 * anything but a `direction` already orthogonal to it. It is Gram-Schmidt'd against
 * `direction` first for that reason, the same construction `Matrix.LookAtLHToRef` itself uses
 * for a camera's view matrix. The guard below is the degenerate case that construction cannot
 * recover from on its own: a near-vertical `forward` in world space, crossed constantly by
 * ordinary arm or mouse movement, collapses the cross product to zero. World forward is
 * swapped in as the reference for exactly that case, and is never itself parallel to a
 * `direction` already (near-)parallel to world up.
 *
 * `direction` is negated before the call despite the name: confirmed by reading
 * `Matrix.LookDirectionLHToRef` and checking it against a Grabbers.test.ts case, its own
 * `forward` argument ends up on the resulting matrix's *third row* (`back`, not `forward` —
 * the function negates its own argument internally), which is what a *view* matrix puts
 * there, not what `TransformNode.forward` reads back off an object's own world matrix (local
 * +Z). Passing `direction` unnegated silently pointed every aimed prop exactly backwards.
 */
function aimRotationFromForward(forward: Vector3): Quaternion {
  const direction = forward.normalizeToNew();
  const up = Vector3.Up();
  const worldForward = Vector3.Forward();

  const reference = Math.abs(Vector3.Dot(direction, up)) > 0.999 ? worldForward : up;

  const right = Vector3.Cross(reference, direction).normalize();
  const rightUp = Vector3.Cross(direction, right).normalize();

  return Quaternion.FromLookDirectionLH(direction.scale(-1), rightUp);
}
